import { fn } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import LineClamp from 'garaje/components/line-clamp';
import dateFormat from 'garaje/helpers/date-format';
import removedString from 'garaje/helpers/removed-string';
import type Entry from 'garaje/models/entry';
import type Invite from 'garaje/models/invite';
import type CurrentLocationService from 'garaje/services/current-location';
import { DISCARDED_RESPONSE_STRINGS } from 'garaje/utils/enums';

interface CustomColumnSignature {
  Args: {
    name: string;
    content: Invite | Entry;
  };
  Element: HTMLDivElement;
}

export default class CustomColumn extends Component<CustomColumnSignature> {
  @service declare currentLocation: CurrentLocationService;
  @tracked nameWasTruncated = false;

  get data(): string {
    const userData = this.args.content.userData;

    if (userData) {
      const matchingField = userData.findBy('field', this.args.name);
      if (matchingField) {
        let val = matchingField.value;
        if (val === DISCARDED_RESPONSE_STRINGS.PLACEHOLDER) {
          val = DISCARDED_RESPONSE_STRINGS.DISPLAY_TEXT;
        }

        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
          return dateFormat(val, 'MMM d, yyyy', { parseFormat: 'yyyy-MM-dd' });
        }

        return val;
      }
    }
    return '';
  }

  <template>
    <div data-test-custom-field class="table-cell border-b align-middle p-2 text-carbon-50" ...attributes>
      <div class="h-12 flex items-center">
        <span data-test-custom-column={{@name}}>
          <LineClamp
            @useNative={{true}}
            @handleTruncate={{fn (mut this.nameWasTruncated)}}
            @lines={{2}}
            @lineHeight={{1.25}}
            @text={{removedString this.data}}
          />
        </span>

        {{#if this.nameWasTruncated}}
          <EmberPopover
            @tooltipClass="break-word max-w-sm"
            @popperContainer=".ui__page"
            @delay={{400}}
            @side="top"
            @text={{removedString this.data}}
          />
        {{/if}}
      </div>
    </div>
  </template>
}
